import React, { useEffect, useState } from "react";
import axios from "axios";
import { Copy, Check, ExternalLink, AlertCircle, CheckCircle2, Rocket, Megaphone, Send, Loader2, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Post-launch checklist for pointing custom domain + rotating webhooks.
 * Persisted in localStorage so the owner sees their progress every visit.
 */
const CUSTOM_DOMAIN = "mamaminassoulfeed.online";
const LS_KEY = "mm.golive.v1";

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={`copy-${label}`}
      className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-[#3a3229] bg-[#1a1512] text-[var(--mm-accent)] hover:bg-[#221a15] transition"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function ChecklistRow({ id, title, children, done, onToggle }) {
  return (
    <div className={`rounded-lg border transition ${done ? "border-emerald-800/50 bg-emerald-950/20" : "border-[#3a3229] bg-[#1a1512]"} p-4`}>
      <div className="flex items-start gap-3">
        <button
          onClick={onToggle}
          data-testid={`golive-toggle-${id}`}
          className={`mt-0.5 h-5 w-5 rounded border flex items-center justify-center transition ${
            done
              ? "bg-emerald-600 border-emerald-600 text-white"
              : "border-[#5a4d40] hover:border-[var(--mm-accent)]"
          }`}
          aria-label={done ? "Mark undone" : "Mark done"}
        >
          {done && <Check size={12} strokeWidth={3} />}
        </button>
        <div className="flex-1">
          <div className={`font-medium text-sm ${done ? "line-through text-neutral-500" : "text-neutral-100"}`}>
            {title}
          </div>
          <div className="text-xs text-neutral-400 mt-2 space-y-2">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function GoLivePanel({ authHeaders = {} }) {
  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const currentTwilioUrl = `${backendOrigin}/api/twilio/inbound-sms`;
  const currentStripeUrl = `${backendOrigin}/api/stripe/webhook`;
  const productionTwilioUrl = `https://${CUSTOM_DOMAIN}/api/twilio/inbound-sms`;
  const productionStripeUrl = `https://${CUSTOM_DOMAIN}/api/stripe/webhook`;

  const [state, setState] = useState({});
  useEffect(() => {
    try { setState(JSON.parse(localStorage.getItem(LS_KEY) || "{}")); }
    catch (err) { console.warn("[golive] checklist hydrate failed", err); }
  }, []);
  const toggle = (id) => {
    const next = { ...state, [id]: !state[id] };
    setState(next);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  };

  const totalSteps = 6;
  const doneCount = Object.values(state).filter(Boolean).length;
  const pct = Math.round((doneCount / totalSteps) * 100);

  return (
    <div className="space-y-4" data-testid="golive-panel">
      <div className="flex items-center gap-3">
        <Rocket className="text-[var(--mm-accent)]" size={20} />
        <div>
          <h3 className="text-lg font-semibold text-neutral-100">Go-Live Checklist</h3>
          <p className="text-xs text-neutral-500">
            Point <code className="text-[var(--mm-accent)]">{CUSTOM_DOMAIN}</code> at your deployment and rotate every webhook.
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="rounded-lg border border-[#3a3229] bg-[#1a1512] p-3">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-neutral-400">Progress</span>
          <span className={`font-mono ${pct === 100 ? "text-emerald-400" : "text-[var(--mm-accent)]"}`}>
            {doneCount}/{totalSteps} · {pct}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-[#0f0b09] overflow-hidden">
          <div
            className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-[var(--mm-accent)]"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {pct === 100 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-emerald-400">
            <CheckCircle2 size={14} /> You&apos;re live. Nice one.
          </div>
        )}
      </div>

      {/* Step 1: DNS */}
      <ChecklistRow
        id="dns"
        title="1. Point DNS at your Emergent deployment"
        done={!!state.dns}
        onToggle={() => toggle("dns")}
      >
        <p>Log in to your domain registrar (Crazy Domains / GoDaddy / Namecheap). Under <strong>DNS Management</strong> add:</p>
        <ul className="list-disc pl-4 space-y-1">
          <li><code>A</code> record · host <code>@</code> · value = the IP Emergent shows in the dashboard&apos;s <em>Link domain</em> tab.</li>
          <li><code>CNAME</code> record · host <code>www</code> · value = <code>soul-food-table.emergent.host</code></li>
        </ul>
        <p className="text-neutral-500">Propagation: ~5-30 min, up to 24h in worst case.</p>
      </ChecklistRow>

      {/* Step 2: Emergent link */}
      <ChecklistRow
        id="link"
        title="2. Verify the domain in Emergent"
        done={!!state.link}
        onToggle={() => toggle("link")}
      >
        <p>Emergent Dashboard → your job → <strong>Deployments</strong> → <strong>Link domain</strong> → paste:</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-2 py-1 rounded bg-[#0f0b09] border border-[#3a3229] text-xs">{CUSTOM_DOMAIN}</code>
          <CopyButton value={CUSTOM_DOMAIN} label="domain" />
        </div>
        <p className="text-neutral-500">SSL cert issues automatically once DNS resolves.</p>
      </ChecklistRow>

      {/* Step 3: Twilio */}
      <ChecklistRow
        id="twilio"
        title="3. Rotate Twilio inbound-SMS webhook"
        done={!!state.twilio}
        onToggle={() => toggle("twilio")}
      >
        <p>Twilio Console → Phone Numbers → your number → <strong>A message comes in</strong> → set to:</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-2 py-1 rounded bg-[#0f0b09] border border-[#3a3229] text-xs break-all">{productionTwilioUrl}</code>
          <CopyButton value={productionTwilioUrl} label="twilio-url" />
        </div>
        <p className="text-neutral-500">Method: HTTP POST. Content type: application/x-www-form-urlencoded.</p>
        <p>
          Currently pointing at: <code className="text-neutral-400 text-[10px] break-all">{currentTwilioUrl}</code>
        </p>
        <a
          href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[var(--mm-accent)] hover:underline"
        >
          Open Twilio console <ExternalLink size={11} />
        </a>
      </ChecklistRow>

      {/* Step 4: Stripe */}
      <ChecklistRow
        id="stripe"
        title="4. Rotate Stripe webhook endpoint"
        done={!!state.stripe}
        onToggle={() => toggle("stripe")}
      >
        <p>Stripe Dashboard → Developers → Webhooks → edit your endpoint URL to:</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-2 py-1 rounded bg-[#0f0b09] border border-[#3a3229] text-xs break-all">{productionStripeUrl}</code>
          <CopyButton value={productionStripeUrl} label="stripe-url" />
        </div>
        <p className="text-neutral-500">
          Listen for: <code>checkout.session.completed</code>, <code>checkout.session.async_payment_succeeded</code>, <code>checkout.session.async_payment_failed</code>, <code>checkout.session.expired</code>, <code>charge.refunded</code>.
        </p>
        <p className="text-amber-400 inline-flex items-start gap-1">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          After saving, copy the new <strong>signing secret</strong> and update <code>STRIPE_WEBHOOK_SECRET</code> in your production .env — then redeploy.
        </p>
        <a
          href="https://dashboard.stripe.com/webhooks"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[var(--mm-accent)] hover:underline"
        >
          Open Stripe webhooks <ExternalLink size={11} />
        </a>
      </ChecklistRow>

      {/* Step 5: Resend */}
      <ChecklistRow
        id="resend"
        title="5. Verify domain in Resend"
        done={!!state.resend}
        onToggle={() => toggle("resend")}
      >
        <p>
          Resend Dashboard → Domains → Add domain → <code>{CUSTOM_DOMAIN}</code>. Copy the SPF, DKIM, and MX records into your registrar&apos;s DNS.
        </p>
        <p className="text-neutral-500">
          Once verified, kitchen tickets and weekly digests can send from <code>hello@{CUSTOM_DOMAIN}</code> to any address — not just the owner.
        </p>
        <a
          href="https://resend.com/domains"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[var(--mm-accent)] hover:underline"
        >
          Open Resend domains <ExternalLink size={11} />
        </a>
      </ChecklistRow>

      {/* Step 6: smoke test */}
      <ChecklistRow
        id="smoketest"
        title="6. Smoke-test the live domain"
        done={!!state.smoketest}
        onToggle={() => toggle("smoketest")}
      >
        <ul className="list-disc pl-4 space-y-1">
          <li>Open <a href={`https://${CUSTOM_DOMAIN}`} target="_blank" rel="noreferrer" className="text-[var(--mm-accent)] underline">https://{CUSTOM_DOMAIN}</a> — home page loads with valid SSL.</li>
          <li>Place a real order using a Stripe test card. Confirm the kitchen ticket email arrives.</li>
          <li>Reply to your Twilio SMS receipt — the note should attach to the order and echo back a confirmation.</li>
          <li>Log in as admin and confirm this checklist and analytics load over the new domain.</li>
        </ul>
      </ChecklistRow>

      <p className="text-[11px] text-neutral-600 pt-2">
        Your progress is saved locally to this browser. Clearing site data resets the checklist (not the real status of your integrations).
      </p>

      <LaunchBlast authHeaders={authHeaders} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Launch Blast — ready-to-send announcement templates                        */
/* -------------------------------------------------------------------------- */
const SMS_TEMPLATE =
  `Mama's kitchen is officially OPEN 🍲 Fresh jollof, peppered soul, straight to your door. ` +
  `First order gets 10% off with code WELCOME10 — order now: https://mamaminassoulfeed.online ` +
  `Reply STOP to opt out.`;

const EMAIL_SUBJECT = "We're officially open — 10% off your first order";

const EMAIL_BODY = `Hey there,

Big news from the kitchen — we're officially live at mamaminassoulfeed.online.

To say thank you for being part of the family since day one, your first order from the new site is 10% off. Just drop the code at checkout:

  → Code: WELCOME10 (10% off your first order)
  → Order: https://mamaminassoulfeed.online

Same menu, same Mama, same peppered soul — cleaner front door and everything runs straight to the kitchen board just like before.

Bookmark it, share it with a friend who hasn't tasted our jollof yet, and come feast with us this week.

With love and pepper,
Mama & the team

--
Order  ·  https://mamaminassoulfeed.online/#order
Book   ·  https://mamaminassoulfeed.online/#reservations
Reply  ·  hello@mamaminassoulfeed.online`;

const SOCIAL_CAPTION = `🎉 WE'RE OPEN — mamaminassoulfeed.online is officially live.

Same Mama. Same peppered soul. Cleaner front door.

First order this week? Take 10% off with code WELCOME10.

Bookmark, tell a friend, and come feast with us.

👉 https://mamaminassoulfeed.online

#MamaminasSoulfeed #Liberian #BrisbaneEats #WestAfricanFood #Halal #GrandOpening`;

function CopyBlock({ label, value, rows = 4, testid }) {
  const [copied, setCopied] = useState(false);
  const chars = value.length;
  return (
    <div className="rounded-lg border border-[#3a3229] bg-[#0f0b09] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-neutral-300">{label}</div>
        <div className="text-[10px] text-neutral-500 font-mono">{chars} chars</div>
      </div>
      <textarea
        readOnly
        rows={rows}
        value={value}
        data-testid={`blast-${testid}`}
        className="w-full bg-[#1a1512] border border-[#2a221c] rounded px-2 py-2 text-xs text-neutral-200 font-mono resize-none focus:outline-none focus:ring-1 focus:ring-[var(--mm-accent)]"
      />
      <button
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        data-testid={`blast-copy-${testid}`}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-[var(--mm-accent)] text-[#1a1512] font-semibold hover:brightness-110 transition"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "Copied" : "Copy to clipboard"}
      </button>
    </div>
  );
}

function LaunchBlast({ authHeaders }) {
  const [msg, setMsg] = useState(SMS_TEMPLATE);
  const [preview, setPreview] = useState(null);   // { recipient_count, sample, opt_out_count, estimated_cost_aud }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);     // { sent_count, failed_count, total_recipients }
  const [history, setHistory] = useState([]);
  const [force, setForce] = useState(false);      // SEC-002: override the 5-min soft cooldown

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await axios.get(`${API}/admin/blast/preview`, { headers: authHeaders });
      setPreview(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blast/history`, { headers: authHeaders });
      setHistory(data.items || []);
    } catch (e) { /* silently ignore — history is nice-to-have */ }
  };

  useEffect(() => {
    if (authHeaders && Object.keys(authHeaders).length) loadHistory();
  }, []);

  const doSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/blast/send`,
        { message: msg, dry_run: false, force },
        { headers: authHeaders }
      );
      setResult(data);
      setConfirming(false);
      setForce(false); // reset override after successful send
      loadHistory();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-[#3a3229] space-y-4">
      <div className="flex items-center gap-3">
        <Megaphone className="text-[var(--mm-accent)]" size={20} />
        <div>
          <h3 className="text-lg font-semibold text-neutral-100">Launch Blast</h3>
          <p className="text-xs text-neutral-500">
            One-click SMS to every loyalty customer. Edit the message, preview who&apos;ll receive it, then send.
          </p>
        </div>
      </div>

      {/* Editable message */}
      <div className="rounded-lg border border-[#3a3229] bg-[#0f0b09] p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-neutral-300">SMS body (editable)</div>
          <div className="text-[10px] text-neutral-500 font-mono">
            {msg.length} chars · ~{Math.ceil(msg.length / 160)} SMS
          </div>
        </div>
        <textarea
          rows={4}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          data-testid="blast-message"
          className="w-full bg-[#1a1512] border border-[#2a221c] rounded px-2 py-2 text-xs text-neutral-200 font-mono resize-none focus:outline-none focus:ring-1 focus:ring-[var(--mm-accent)]"
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={loadPreview}
          disabled={loadingPreview}
          data-testid="blast-preview-btn"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded border border-[#3a3229] bg-[#1a1512] text-neutral-200 hover:bg-[#221a15] disabled:opacity-50 transition"
        >
          {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
          Preview recipients
        </button>
        <button
          onClick={() => setConfirming(true)}
          disabled={!preview || preview.recipient_count === 0}
          data-testid="blast-send-btn"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded bg-[var(--mm-accent)] text-[#1a1512] font-semibold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <Send size={14} />
          Send blast{preview && preview.recipient_count > 0 ? ` (${preview.recipient_count})` : ""}
        </button>
      </div>

      {/* Preview card */}
      {preview && (
        <div data-testid="blast-preview" className="rounded-lg border border-[#3a3229] bg-[#0f0b09] p-4 text-xs space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-neutral-300 font-semibold">
              {preview.recipient_count} recipient{preview.recipient_count === 1 ? "" : "s"}
            </div>
            <div className="text-neutral-500">
              est. cost <span className="text-[var(--mm-accent)]">A${preview.estimated_cost_aud.toFixed(2)}</span>
              {preview.opt_out_count > 0 && (
                <span className="ml-2">· {preview.opt_out_count} opted out</span>
              )}
            </div>
          </div>
          {preview.sample && preview.sample.length > 0 && (
            <div className="pt-2 border-t border-[#2a221c] space-y-1 text-neutral-400">
              <div className="text-[10px] uppercase tracking-wider text-neutral-600">Sample of first 5</div>
              {preview.sample.map((s, i) => (
                <div key={i} className="flex justify-between font-mono text-[11px]">
                  <span>{s.name}</span>
                  <span className="text-neutral-500">{s.phone_masked}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirmation modal */}
      {confirming && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="blast-confirm-modal">
          <div className="max-w-md w-full rounded-lg bg-[#1a1512] border border-[var(--mm-accent)] p-5 space-y-3">
            <h4 className="text-base font-semibold text-neutral-100 flex items-center gap-2">
              <AlertCircle className="text-[var(--mm-accent)]" size={16} />
              Send to {preview.recipient_count} customers?
            </h4>
            <p className="text-xs text-neutral-400">
              Twilio will charge approximately <strong>A${preview.estimated_cost_aud.toFixed(2)}</strong>. This can&apos;t be un-sent.
              Only send this after your custom domain is live and SSL is green.
            </p>
            <div className="p-2 rounded bg-[#0f0b09] border border-[#3a3229] text-[11px] font-mono text-neutral-300 max-h-24 overflow-y-auto">
              {msg}
            </div>
            <label className="flex items-center gap-2 text-[11px] text-neutral-400 pt-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                data-testid="blast-force-toggle"
                className="rounded accent-[var(--mm-accent)]"
              />
              Override the 5-minute cooldown (send anyway — I know what I&apos;m doing)
            </label>
            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={() => setConfirming(false)}
                disabled={sending}
                data-testid="blast-cancel-btn"
                className="text-sm px-3 py-1.5 rounded border border-[#3a3229] text-neutral-300 hover:bg-[#221a15]"
              >
                Cancel
              </button>
              <button
                onClick={doSend}
                disabled={sending}
                data-testid="blast-confirm-btn"
                className="text-sm px-3 py-1.5 rounded bg-[var(--mm-accent)] text-[#1a1512] font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {sending ? "Sending…" : "Yes, send it"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div
          data-testid="blast-result"
          className={`rounded-lg border p-3 text-xs ${
            result.error
              ? "border-red-800/50 bg-red-950/30 text-red-300"
              : "border-emerald-800/50 bg-emerald-950/20 text-emerald-300"
          }`}
        >
          {result.error ? (
            <><AlertCircle size={12} className="inline mr-1" /> {result.error}</>
          ) : result.status === "queued" ? (
            <>
              <CheckCircle2 size={12} className="inline mr-1" />
              Queued <strong>{result.queued_count}</strong> SMS for delivery
              {result.truncated_by_cap && (
                <span className="text-amber-400 ml-2">
                  (truncated to daily cap)
                </span>
              )}
              . Refresh history in a minute to see final delivered/failed counts.
            </>
          ) : (
            <>
              <CheckCircle2 size={12} className="inline mr-1" />
              Delivered to <strong>{result.sent_count}</strong> of {result.total_recipients}.
              {result.failed_count > 0 && (
                <span className="text-amber-400 ml-2">
                  {result.failed_count} failed (bad number / opt-out / Twilio error).
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Copy templates for owner to paste elsewhere (email + social) */}
      <details className="rounded-lg border border-[#3a3229] bg-[#0f0b09] p-3">
        <summary className="text-xs font-semibold text-neutral-300 cursor-pointer select-none">
          Copy templates for other channels (email · social)
        </summary>
        <div className="mt-3 space-y-3">
          <CopyBlock label="Email · Subject" value={EMAIL_SUBJECT} rows={1} testid="email-subject" />
          <CopyBlock label="Email · Body" value={EMAIL_BODY} rows={12} testid="email-body" />
          <CopyBlock label="Instagram / Facebook caption" value={SOCIAL_CAPTION} rows={8} testid="social" />
        </div>
      </details>

      {/* Recent blast history */}
      {history.length > 0 && (
        <details className="rounded-lg border border-[#3a3229] bg-[#0f0b09] p-3" open>
          <summary className="text-xs font-semibold text-neutral-300 cursor-pointer select-none">
            Recent blasts ({history.length})
          </summary>
          <div className="mt-3 space-y-2">
            {history.map((h) => (
              <div key={h.campaign_id} className="text-[11px] rounded border border-[#2a221c] p-2 bg-[#1a1512]">
                <div className="flex justify-between text-neutral-400 font-mono">
                  <span>{h.created_at?.slice(0, 16).replace("T", " ")}</span>
                  <span>
                    <span className="text-emerald-400">{h.sent_count}</span>
                    {" / "}
                    <span className="text-neutral-500">{h.total_recipients}</span>
                    {h.failed_count > 0 && (
                      <span className="text-amber-400"> · {h.failed_count} failed</span>
                    )}
                  </span>
                </div>
                <div className="mt-1 text-neutral-300 line-clamp-2">{h.message}</div>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="rounded-lg border border-amber-900/50 bg-amber-950/30 p-3 flex items-start gap-2 text-xs text-amber-300">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <span>
          Recipients: everyone with a phone on file (registered users + guest orders), de-duped
          and filtered against the opt-out list. Send only after your custom domain is live —
          otherwise the link in the SMS breaks.
        </span>
      </div>
    </div>
  );
}
